import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, Bookmark, Unlock, Crown, Compass, User, Users, MapPin, ArrowRight, CalendarDays } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { VerifiedBadge } from "../components/kit";

const QUICK_ACTIONS = [
  { label: "Explore Opportunities", to: "/opportunities", icon: Search, testid: "qa-search" },
  { label: "Saved Companies", to: "/dashboard#saved", icon: Bookmark, testid: "qa-saved" },
  { label: "My Contacts", to: "/dashboard#contacts", icon: Unlock, testid: "qa-contacts" },
  { label: "My Network", to: "/connections", icon: Users, testid: "qa-network" },
  { label: "Opportunities", to: "/opportunities", icon: Compass, testid: "qa-opportunities" },
  { label: "Membership", to: "/membership", icon: Crown, testid: "qa-membership" },
  { label: "Events", to: "/events", icon: User, testid: "qa-events" },
];

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [saved, setSaved] = useState([]);
  const [unlocks, setUnlocks] = useState([]);

  useEffect(() => { if (user === false) navigate("/login"); }, [user, navigate]);
  useEffect(() => {
    if (!user) return;
    api.get("/my/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/my/saved").then((r) => setSaved(r.data)).catch(() => {});
    api.get("/my/unlocks").then((r) => setUnlocks(r.data)).catch(() => {});
  }, [user]);

  if (!user || !stats) return <div className="max-w-7xl mx-auto px-8 py-24 text-sm font-mono text-slate-500 animate-pulse" data-testid="dashboard-loading">Loading your business intelligence…</div>;

  const hour = new Date().getHours();
  const greeting = hour < 12 ? "GOOD MORNING" : hour < 17 ? "GOOD AFTERNOON" : "GOOD EVENING";
  const isMember = ["ACTIVE MEMBER", "APPROVED"].includes(stats.membership_status);
  const memberId = `10XG-${String(user.id || user._id || "").slice(-6).toUpperCase()}`;
  const validTill = user.created_at
    ? new Date(new Date(user.created_at).setFullYear(new Date(user.created_at).getFullYear() + 1)).toLocaleDateString("en-GB")
    : "—";

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-12" data-testid="dashboard-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
        <div>
          <p className="gold-kicker mb-2">{greeting},</p>
          <h1 className="font-display text-3xl md:text-4xl font-bold text-slate-100" data-testid="dashboard-company-name">{(user.company || user.name || "").toUpperCase()}</h1>
          <div className="mt-2 flex items-center gap-3">
            <VerifiedBadge level={user.verification_level || "REGISTERED"} testid="dashboard-verification-badge" />
            <span className={`text-[10px] font-mono uppercase tracking-widest ${isMember ? "text-gold" : "text-slate-500"}`} data-testid="dashboard-plan">{stats.plan} PLAN</span>
          </div>
        </div>
        <Link to="/opportunities" className="btn-gold text-xs" data-testid="dashboard-search-btn">EXPLORE OPPORTUNITIES</Link>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4" data-testid="dashboard-stats">
        {[
          ["Companies Viewed", stats.companies_viewed, "stat-viewed"],
          ["Companies Saved", stats.saved_companies, "stat-saved"],
          ["Contacts Unlocked", stats.contacts_unlocked, "stat-unlocked"],
          ["Searches Used", stats.searches, "stat-searches"],
          ["Contact Credits Left", stats.credits_remaining, "stat-credits"],
          ["Membership", stats.plan, "stat-membership"],
        ].map(([label, value, tid]) => (
          <div key={tid} className="card-surface p-5" data-testid={tid}>
            <p className="font-mono text-2xl font-bold text-gold">{typeof value === "number" ? value.toLocaleString() : value}</p>
            <p className="mt-1.5 text-[10px] font-mono uppercase tracking-widest text-slate-500">{label}</p>
          </div>
        ))}
      </div>

      {!isMember && (
        <div className="card-surface hairline-gold p-6 md:p-8 mt-8 flex flex-wrap items-center justify-between gap-6" data-testid="membership-upsell">
          <div className="max-w-2xl">
            <p className="text-[10px] font-mono uppercase tracking-widest text-gold mb-2">Member Value</p>
            <p className="text-slate-200 leading-relaxed">Don't just find companies — know who to connect with. Unlock verified business contact intelligence with membership.</p>
          </div>
          <Link to="/membership" className="btn-gold text-xs" data-testid="upgrade-membership-btn">EXPLORE MEMBERSHIP <ArrowRight size={14} /></Link>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3 mt-8" data-testid="quick-actions">
        {QUICK_ACTIONS.map((a) => (
          <Link key={a.testid} to={a.to} data-testid={a.testid} className="card-surface p-4 flex flex-col items-center gap-2.5 hover:border-gold/40 transition-colors group">
            <a.icon size={18} className="text-slate-500 group-hover:text-gold transition-colors" />
            <span className="text-[10px] font-mono uppercase tracking-widest text-slate-400 group-hover:text-slate-200">{a.label}</span>
          </Link>
        ))}
      </div>

      {/* MEMBERSHIP CERTIFICATE */}
      <div className="mt-10" data-testid="certificate-section">
        <div className="flex items-center justify-between mb-4">
          <p className="text-xs font-mono uppercase tracking-widest text-slate-500">Membership Certificate</p>
          <button onClick={() => window.print()} data-testid="certificate-print-btn" className="btn-outline !px-4 !py-2 text-xs">PRINT / SAVE PDF</button>
        </div>
        <div className="relative overflow-hidden rounded-sm" data-testid="membership-certificate" style={{ border: "2px solid #D4AF37", containerType: "inline-size" }}>
          <img src="https://customer-assets-eiarnc6j.emergentagent.net/job_b2b-intelligence-2/artifacts/jj6ssbp9_IMG_20260831_182211.png" alt="10XGLOBAL Certificate of Membership" className="w-full block" data-testid="certificate-art" />
          {/* member name — covers the placeholder script in the artwork */}
          <div className="absolute flex items-center justify-center" style={{ left: "10.5%", top: "47.5%", width: "52%", height: "14.5%", background: "#040A1A", borderBottom: "1px solid rgba(212,175,55,0.35)" }}>
            <p className="font-display italic font-bold bg-gradient-to-b from-[#F9E9B8] via-[#D4AF37] to-[#B08A2E] bg-clip-text text-transparent truncate px-3" style={{ fontSize: "4.8cqw", lineHeight: 1.2 }} data-testid="certificate-company">{user.company || user.name || ""}</p>
          </div>
          {/* membership ID — inside the artwork badge, covering the placeholder ID */}
          <div className="absolute flex items-center justify-center" style={{ left: "21%", top: "82.3%", width: "28.5%", height: "6.5%", background: "#060D20" }}>
            <p className="font-mono font-bold text-white tracking-wider truncate" style={{ fontSize: "2.5cqw" }} data-testid="certificate-id">{memberId}</p>
          </div>
          {/* validity — dark stage just under the badge */}
          <p className="absolute text-center font-mono text-slate-300/80" style={{ left: "19%", top: "93.6%", width: "33%", fontSize: "1.6cqw" }} data-testid="certificate-valid">VALID TILL {validTill}</p>
          {/* verification QR — bottom-left corner */}
          <div className="absolute flex flex-col items-center gap-1" style={{ left: "3.5%", bottom: "3%", width: "9.5%" }}>
            <img src={`https://api.qrserver.com/v1/create-qr-code/?size=120x120&data=${encodeURIComponent(`https://10x-global.com/verify/${memberId}`)}`}
              alt="Scan to verify membership" className="w-full border border-[#D4AF37]/70 rounded-sm bg-white p-0.5" data-testid="certificate-qr" />
            <p className="font-mono text-[#D4AF37] tracking-widest" style={{ fontSize: "1.5cqw" }}>SCAN TO VERIFY</p>
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6 mt-10">
        <div id="saved">
          <p className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-4">Saved Companies</p>
          <div className="space-y-3" data-testid="saved-list">
            {saved.length === 0 && <div className="card-surface p-6 text-sm text-slate-500" data-testid="saved-empty">No saved companies yet — save companies from their profile page.</div>}
            {saved.map((co) => (
              <div key={co.id} className="card-surface p-4 flex items-center gap-4" data-testid={`saved-${co.id}`}>
                <span className="w-10 h-10 rounded-sm bg-navy-700 border border-slate-700 flex items-center justify-center font-display font-bold text-gold shrink-0">{co.name.charAt(0)}</span>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-slate-100 truncate">{co.name}</p>
                  <p className="text-[11px] text-slate-500 flex items-center gap-1"><MapPin size={10} />{[co.location, co.country].filter(Boolean).join(", ") || co.industry}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div id="contacts">
          <p className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-4">Unlocked Contact Intelligence</p>
          <div className="space-y-3" data-testid="unlocks-list">
            {unlocks.length === 0 && <div className="card-surface p-6 text-sm text-slate-500" data-testid="unlocks-empty">No contacts unlocked yet. {isMember ? "Unlock verified contacts from any company profile." : "Membership required to unlock contacts."}</div>}
            {unlocks.map((u) => (
              <div key={u.company_id} className="card-surface p-4" data-testid={`unlock-${u.company_id}`}>
                <div className="flex items-center justify-between gap-3">
                  <p className="text-sm font-semibold text-slate-100 truncate">{u.company_name}</p>
                  <span className="text-[9px] font-mono uppercase tracking-widest text-emerald-400 border border-emerald-500/30 px-2 py-0.5 rounded-sm shrink-0">{u.verification?.status}</span>
                </div>
                <p className="text-[11px] font-mono text-slate-500 mt-1">Unlocked {String(u.ts).slice(0, 10)} · {u.plan} plan · audited</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
